import {ChangeDetectionStrategy, Component, input, output} from '@angular/core';
import {DatePipe} from '@brunata-angular/ui';
import {TranslatePipe} from '@brunata-angular/util';
import {SavedExport} from '../saved-export.model';
import {FileFormatBadgeComponent} from '../../shared/ui/file-format-badge/file-format-badge.component';
import {ExportRowActionsComponent} from '../../shared/ui/export-row-actions/export-row-actions.component';
import {DeliveryStatusComponent} from '../delivery-status/delivery-status.component';
import {ExportNowButtonComponent} from '../export-now-button/export-now-button.component';

@Component({
    selector: 'app-saved-export-card',
    standalone: true,
    imports: [
        DatePipe,
        TranslatePipe,
        FileFormatBadgeComponent,
        ExportRowActionsComponent,
        DeliveryStatusComponent,
        ExportNowButtonComponent,
    ],
    template: `
        <div class="card h-100">
            <div
                class="card-title tile-header me-0 ps-3 d-flex align-items-center justify-content-between gap-2">
                <h3
                    class="pe-2 fs-6 mb-0"
                    [class.text-break]="!oneLineName()"
                    [class.text-truncate]="oneLineName()"
                    [attr.title]="oneLineName() ? savedExport().name : null">
                    {{ savedExport().name }}
                </h3>
                <div class="p-1">
                    <app-export-row-actions
                        [showDuplicate]="true"
                        (edit)="edit.emit(savedExport())"
                        (duplicate)="duplicate.emit(savedExport())"
                        (delete)="delete.emit(savedExport())" />
                </div>
            </div>

            <div class="card-body">
                <ul class="list-group list-group-flush description-list w-100">
                    @if (savedExport().outputFormat; as outputFormat) {
                        <li class="list-group-item align-items-center">
                            <span>
                                {{
                                    'savedexport_savedexports_column_format'
                                        | translate: 'online' : 'export'
                                }}
                            </span>
                            <app-file-format-badge [format]="outputFormat" />
                        </li>
                    }
                    <li class="list-group-item align-items-center">
                        <span>
                            {{
                                'savedexport_savedexports_column_schedule'
                                    | translate: 'online' : 'export'
                            }}
                        </span>
                        <span>{{
                            savedExport().scheduleLabelKey | translate: 'online' : 'export'
                        }}</span>
                    </li>
                    @if (deliveryLog()) {
                        <li class="list-group-item align-items-center">
                            <span>
                                {{
                                    'savedexport_savedexports_column_status'
                                        | translate: 'online' : 'export'
                                }}
                            </span>
                            <app-delivery-status
                                [savedExport]="savedExport()"
                                [wrap]="true"
                                (viewLog)="viewLog.emit(savedExport())" />
                        </li>
                    }
                    <li class="list-group-item align-items-center">
                        <span>{{ 'reporting_START_DATE' | translate }}</span>
                        <span>
                            {{
                                savedExport().startDate != null
                                    ? (savedExport().startDate | appdate)
                                    : '-'
                            }}
                        </span>
                    </li>
                </ul>
            </div>

            <div class="card-footer bg-white d-flex justify-content-end">
                <app-export-now-button
                    [exporting]="exporting()"
                    [deliveryRequested]="deliveryRequested()"
                    (run)="export.emit(savedExport())" />
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedExportCardComponent {
    readonly savedExport = input.required<SavedExport>();
    readonly deliveryLog = input(false);
    readonly oneLineName = input(false);
    readonly exporting = input(false);
    readonly deliveryRequested = input(false);
    readonly export = output<SavedExport>();
    readonly edit = output<SavedExport>();
    readonly duplicate = output<SavedExport>();
    readonly delete = output<SavedExport>();
    readonly viewLog = output<SavedExport>();
}
